import { useRef } from "react";
import Popup from "reactjs-popup";

import ModalConfirm from "../../general/auxiliar/modal/ModalConfirm";
import useFormWithDisable from "../../../auxiliar/customHooks/useFormWithDisable";
import CustomForm from "../../general/form/CustomForm";
import useScreenSize from "../../../auxiliar/customHooks/useScreenSize";
import { deleteLogo, updateLogo } from "../../../actions/clinics";
import { checkLogo } from "../../../auxiliar/formatValidators";
import { onClosePopup, onOpenPopup } from "../../../auxiliar/auxFunctions";

const EditLogoPopup = ({ originalLogoURL, clinicId, refreshLogoUrl, trigger }) => {
    const ref = useRef();
    const closeTooltip = () => ref.current.close();
    const { width: windowWidth } = useScreenSize();
    const modalId = "delete-logo-modal";

    const {
        register,
        handleSubmit,
        handleSubmitWithoutValidation,
        openModal,
        errors,
        isSubmitting,
    } = useFormWithDisable(modalId);

    const formConfig = {
        logo: {
            condition: true,
            disabled: false,
            name: "logo",
            info: "Archivo jpg o png de menos de 25MB",
            label: "Nuevo logo",
            type: "file",
            tooltipLocation: "tooltip-right",
            validators: {
                required: "El logo es obligatorio",
                validate: checkLogo,
            },
        },
    };

    const closeTrigger = (logoUrl = "deleted") => {
        refreshLogoUrl(logoUrl);
        closeTooltip();
    };

    return (
        <Popup
            onClose={onClosePopup()}
            onOpen={onOpenPopup()}
            ref={ref}
            modal
            position="bottom center"
            lockScroll
            nested
            closeOnDocumentClick={false}
            trigger={trigger}
            contentStyle={{ width: windowWidth > 768 ? "50%" : "90%" }}
        >
            <div
                className="p-5 bg-base-100 rounded-lg scrollbar-thumb-rounded-full
                    scrollbar-track-rounded-full scrollbar-thin scrollbar-thumb-base-300
                    scrollbar-track-base-100 overflow-x-hidden"
                style={{ width: "100%", maxHeight: "100%", overflowY: "auto" }}
            >
                <div className="p-5 bg-base-100 rounded-lg">
                    {originalLogoURL ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div className="col-span-3">
                                    <h3 className="text-2xl text-primary">Editar logo</h3>
                                    <p className="lg:text-lg my-3">
                                        Puedes actualizar el logo de tu clínica. Este se mostrará en
                                        las reservas online, facturas y otros puntos visibles para
                                        tus pacientes. Asegúrate de usar un archivo PNG o JPG.
                                    </p>
                                </div>
                                <span className="flex flex-col items-center justify-center">
                                    <img
                                        className="object-cover h-36 mx-auto"
                                        src={originalLogoURL}
                                        alt="Logo actual"
                                    />

                                    <p className="text-sm ">Logo actual</p>
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="text-2xl text-primary">Añadir logo</h3>

                            <p className="lg:text-lg my-3">
                                Sube el logo de tu clínica para que se muestre en las reservas
                                online, facturas y otros elementos visibles para tus pacientes. El
                                archivo debe ser PNG o JPG.
                            </p>
                        </>
                    )}

                    <form className="my-5">
                        <CustomForm formConfig={formConfig} errors={errors} register={register} />

                        <div className="mt-5 flex space-x-2 sm:space-x-4">
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSubmit(updateLogo, clinicId, closeTrigger)}
                                disabled={isSubmitting}
                            >
                                Guardar
                            </button>

                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSubmitWithoutValidation(closeTooltip)}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>

                            {originalLogoURL && (
                                <button
                                    className="btn btn-error btn-sm"
                                    onClick={openModal}
                                    disabled={isSubmitting}
                                >
                                    Eliminar
                                </button>
                            )}
                        </div>
                    </form>

                    <ModalConfirm
                        id={modalId}
                        title="¿Estás seguro de que quieres eliminar el logo de la clínica?"
                        text="No podremos recuperar la información más adelante."
                        onConfirm={handleSubmitWithoutValidation(
                            deleteLogo,
                            clinicId,
                            closeTrigger,
                        )}
                    />
                </div>
            </div>
        </Popup>
    );
};

export default EditLogoPopup;
